import React, { useState, useEffect } from "react";
import { CartoonCharacterSelector } from "./CartoonCharacterSelector";

type Scene = { id: number; prompt: string; altText: string };

interface Props {
  storyId: string;
  childAge: number;
  scenes: Scene[];
  selectedCharacter?: any; // Add selected character prop
  onCharacterSelected?: (character: any) => void;
  onGenerated: (payload: any) => void;
}

export const IllustrationsTogglePanel: React.FC<Props> = ({ storyId, childAge, scenes, selectedCharacter: propSelectedCharacter, onCharacterSelected, onGenerated }) => {
  const [generate, setGenerate] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState<any | null>(propSelectedCharacter || null);
  const [submitting, setSubmitting] = useState(false);
  const [progressMsg, setProgressMsg] = useState<string | null>(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });
  const [generatedIllustrations, setGeneratedIllustrations] = useState<any[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  // Sync prop with local state
  useEffect(() => {
    if (propSelectedCharacter) {
      setSelectedCharacter(propSelectedCharacter);
    }
  }, [propSelectedCharacter]);

  const handleGenerate = async () => {
    setSubmitting(true);
    setProgressMsg("Starting generation…");
    setProgress({ completed: 0, total: scenes.length });
    setErrors([]);
    setGeneratedIllustrations([]);
    
    try {
      const payload: any = {
        storyId,
        childAge,
        scenes,
      };
      if (selectedCharacter?.id) {
        payload.selectedCharacterId = selectedCharacter.id.toString();
      } else if (selectedCharacter?.cartoonImageUrl) {
        payload.uploadedCharacterImageUrl = selectedCharacter.cartoonImageUrl;
      }
      
      // Simulate progress updates (in real implementation, this would come from SSE or polling)
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          const newCompleted = Math.min(prev.completed + 1, prev.total);
          setProgressMsg(`Generating illustration ${newCompleted} of ${prev.total}...`);
          return { ...prev, completed: newCompleted };
        });
      }, 2000);
      
      const res = await fetch("/api/illustrations/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      
      clearInterval(progressInterval);
      
      const j = await res.json();
      if (res.ok) {
        setGeneratedIllustrations(j.results || []);
        setProgress({ completed: scenes.length, total: scenes.length });
        setProgressMsg("Generation complete!");
        onGenerated(j);
      } else {
        throw new Error(j?.error || "Failed to generate");
      }
    } catch (e: any) {
      setErrors([e?.message || "Generation failed"]);
      setProgressMsg("Generation failed");
    } finally {
      setSubmitting(false);
      setTimeout(() => setProgressMsg(null), 3000);
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={generate} onChange={e => setGenerate(e.target.checked)} />
          <span className="font-medium">Generate Illustrations with Custom Characters</span>
        </label>
        {!selectedCharacter?.id && (
          <button
            onClick={handleGenerate}
            disabled={!generate || !selectedCharacter || submitting}
            className={`px-3 py-2 rounded ${(!generate || !selectedCharacter || submitting) ? "bg-gray-300" : "bg-blue-600 text-white"}`}
          >
            {submitting ? "Generating…" : "Generate Now"}
          </button>
        )}
      </div>

      <CartoonCharacterSelector
        isEnabled={generate}
        onCharacterSelected={c => {
          setSelectedCharacter(c);
          onCharacterSelected?.(c);
        }}
        selectedCharacter={selectedCharacter}
      />

      {generate && !selectedCharacter && (
        <p className="text-sm text-gray-600">Select or upload a character first.</p>
      )}
      
      {/* Progress Bar */}
      {submitting && progress.total > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>{progressMsg}</span>
            <span>{progress.completed}/{progress.total}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(progress.completed / progress.total) * 100}%` }}
            />
          </div>
        </div>
      )}
      
      {/* Error Messages */}
      {errors.length > 0 && (
        <div className="space-y-2">
          {errors.map((error, index) => (
            <div key={index} className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700">
              <span className="text-sm">⚠️</span>
              <span className="text-sm">{error}</span>
              <button 
                onClick={() => setErrors(prev => prev.filter((_, i) => i !== index))}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Generated Illustrations Preview */}
      {generatedIllustrations.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Generated Illustrations:</h4>
          <div className="grid grid-cols-2 gap-2">
            {generatedIllustrations.map((illustration, index) => (
              <div key={index} className="relative">
                <img 
                  src={illustration.imageUrl} 
                  alt={illustration.altText}
                  className="w-full h-24 object-cover rounded border"
                />
                {illustration.isFallback && (
                  <div className="absolute top-1 right-1 bg-yellow-500 text-white text-xs px-1 rounded">
                    Fallback
                  </div>
                )}
                {illustration.errorCode && (
                  <div className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1 rounded">
                    Error
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {progressMsg && !submitting && (
        <p className="text-sm text-gray-600">{progressMsg}</p>
      )}
    </div>
  );
};
